import { useCallback, useEffect, useLayoutEffect, type FormEvent, type RefObject } from 'react'
import { ChatFilesDropdown } from '@/agent-v2/components/ChatFilesDropdown'
import { ComposerFileChips } from '@/agent-v2/components/ComposerFileChips'
import { Plus } from '@/assets/icons'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import type { MenuContentProps } from '@/components/menu/MenuContent'
import SourcesControls from '@/agent-v2/chat-form/components/SourcesControls'
import FileActionMenu from '@/chat-common/components/form-source-controls/assistant/FileActionMenu'
import UserSkillPicker from '@/user-skills/components/UserSkillPicker'
import SelectedSkills from '@/user-skills/components/SelectedSkills'
import { useUserSkills } from '@/user-skills/hooks/useUserSkills'
import ChatInputButton from '@/agent-v2/components/ChatInputButton'
import VoiceInputButton from '@/agent-v2/components/VoiceInputButton'
import type { DictationSurface } from '@/analytics/enums/dictation.enums'
import { kAgentV2LongInputWarningChars, kAgentV2MaxTurnInputChars } from '@/agent-v2/utils/agent-v2-contract'

type ChatInputBoxProps = {
  conversationId: string
  conversationSubmitBlockedReason: string | null
  errorMessage: string | null
  disableActions: boolean
  hasAttachedSkills: boolean
  hasUnreadyUserFiles: boolean
  inputText: string
  isRecovering: boolean
  recoveryStartedAfterDetachment: boolean
  isRunning: boolean
  isStopping: boolean
  matterId: string | null
  matterSubmitBlockedReason: string | null
  onInputTextChange: (text: string) => void
  onStop: () => void
  onSubmit: (event?: FormEvent<HTMLFormElement>) => void
  runControlButtonRef: RefObject<HTMLButtonElement>
  textAreaRef: RefObject<HTMLTextAreaElement>
  placeholder?: string
  disableInput?: boolean
  onVoiceTranscription?: (text: string) => void
  dictationSurface: DictationSurface
  onOpenMatterSettings?: (matterId: string) => void
  onOpenGlobalSettings?: () => void
  skillsMenuSide?: MenuContentProps['side']
}

export default function ChatInputBox({
  conversationId,
  conversationSubmitBlockedReason,
  errorMessage,
  disableActions,
  hasAttachedSkills,
  hasUnreadyUserFiles,
  inputText,
  isRecovering,
  recoveryStartedAfterDetachment,
  isRunning,
  isStopping,
  matterId,
  matterSubmitBlockedReason,
  onInputTextChange,
  onStop,
  onSubmit,
  runControlButtonRef,
  textAreaRef,
  placeholder = 'Ask a question, start a draft, or research a legal issue',
  disableInput = false,
  onVoiceTranscription,
  dictationSurface,
  onOpenMatterSettings,
  onOpenGlobalSettings,
  skillsMenuSide,
}: ChatInputBoxProps) {
  const skillsLibrary = useUserSkills()
  const sizeTextAreaToContent = useCallback(() => {
    const textArea = textAreaRef.current
    // Skip a zero-width textarea (hidden but still mounted): its placeholder wraps one character per line, so measuring it would stick the box at max height.
    if (!textArea || textArea.clientWidth === 0) return
    textArea.style.height = 'auto'
    textArea.style.height = `${textArea.scrollHeight}px`
  }, [textAreaRef])

  // Before paint, so text the box opens with is never shown at one row first.
  useLayoutEffect(() => {
    sizeTextAreaToContent()
  }, [inputText, sizeTextAreaToContent])

  /**
   * Size the box again whenever its width changes, since the text wraps differently and needs a new
   * height: a surface narrowing around it, and a hidden box coming back into view.
   */
  useEffect(() => {
    const textArea = textAreaRef.current
    if (!textArea) return

    let lastWidth = textArea.clientWidth
    const resizeObserver = new ResizeObserver(() => {
      // Height changes come from the sizing itself, so only a width change calls for a re-measure.
      if (textArea.clientWidth === lastWidth) return
      lastWidth = textArea.clientWidth
      sizeTextAreaToContent()
    })
    resizeObserver.observe(textArea)
    return () => resizeObserver.disconnect()
  }, [sizeTextAreaToContent, textAreaRef])

  const focusTextArea = useCallback(() => textAreaRef.current?.focus(), [textAreaRef])

  const characterCount = inputText.trim().length
  const inputExceedsCap = characterCount > kAgentV2MaxTurnInputChars
  const showInputLengthGuidance = characterCount >= kAgentV2LongInputWarningChars
  const canSubmit =
    (inputText.trim().length > 0 || hasAttachedSkills) &&
    !inputExceedsCap &&
    !isRunning &&
    !isRecovering &&
    !disableActions &&
    !matterSubmitBlockedReason &&
    !conversationSubmitBlockedReason &&
    !hasUnreadyUserFiles

  return (
    <div className="relative mx-auto w-full max-w-3xl">
      <ChatFilesDropdown conversationId={conversationId} onLastFileRemoved={focusTextArea} />
      <div className="agent-v2-input-field relative w-full rounded-lg border border-border-default bg-surface-field shadow-[0px_0px_12px_0px_rgba(95,96,88,0.1)]">
        <form onSubmit={onSubmit}>
          {/* The padding lives in --agent-v2-input-padding so the file chip strip can run edge to edge across it, and a surface
              that restyles the input (e.g. the Outlook task pane) changes both by overriding the variable */}
          <div className="agent-v2-input-content grid grid-rows-[1fr_auto] items-end gap-3 p-[var(--agent-v2-input-padding)] [--agent-v2-input-padding:10px] xs:[--agent-v2-input-padding:16px]">
            {/* min-w-0 lets this grid cell shrink to the input, so wide chip rows scroll instead of widening it */}
            <div className="min-w-0 self-center">
              <ComposerFileChips conversationId={conversationId} onLastFileRemoved={focusTextArea} />
              <SelectedSkills conversationId={conversationId} disabled={isRunning || isRecovering || disableActions} library={skillsLibrary} />
              <textarea
                ref={textAreaRef}
                aria-label="Ask a question"
                className="m-0 min-h-11 w-full resize-none self-center overflow-y-auto border-0 bg-transparent p-0 pt-1 text-base leading-6 placeholder:text-text-tertiary focus:ring-0"
                disabled={disableInput}
                onChange={(event) => onInputTextChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    event.currentTarget.form?.requestSubmit()
                  }
                }}
                placeholder={placeholder}
                rows={1}
                style={{ maxHeight: 'clamp(6rem, 30dvh, 22rem)' }}
                value={inputText}
              />
            </div>

            <div className="agent-v2-input-actions grid grid-cols-[1fr_auto] items-end [container-type:inline-size]">
              <div className="flex min-w-0 items-center gap-3">
                <FileActionMenu
                  disabled={isRunning || disableActions}
                  conversationId={conversationId}
                  icon={<Plus />}
                  size="md"
                  align="start"
                  onOpenMatterSettings={onOpenMatterSettings}
                  onOpenGlobalSettings={onOpenGlobalSettings}
                />
                {/* v2 has no persisted-filters restore to wait for, so prefill is ready immediately (ENG-7074) */}
                <SourcesControls
                  conversationId={conversationId}
                  matterId={matterId}
                  prefillReady
                  triggerLabel="Research"
                  showWebSearch
                  disabled={disableActions}
                  responsiveLabel
                />
                <UserSkillPicker
                  conversationId={conversationId}
                  disabled={isRunning || isRecovering || disableActions}
                  side={skillsMenuSide}
                  library={skillsLibrary}
                />
              </div>

              <div className="flex items-center justify-end gap-x-3">
                {onVoiceTranscription && (
                  <VoiceInputButton
                    composerHadText={inputText.trim().length > 0}
                    conversationId={conversationId || undefined}
                    dictationSurface={dictationSurface}
                    disabled={isRunning || disableActions}
                    onTranscription={onVoiceTranscription}
                  />
                )}
                <ChatInputButton ref={runControlButtonRef} canSubmit={canSubmit} isRunning={isRunning} isStopping={isStopping} onStop={onStop} />
              </div>
            </div>
          </div>
        </form>
      </div>
      {showInputLengthGuidance && (
        <div className="mt-1">
          <InlineMessage variant={inputExceedsCap ? 'error' : 'neutral'}>
            {`${characterCount.toLocaleString('en-US')} / ${kAgentV2MaxTurnInputChars.toLocaleString('en-US')} characters`}
            {inputExceedsCap
              ? ' — Message too long. Try attaching long materials as a document.'
              : ' — for better results, attach long source material as a document.'}
          </InlineMessage>
        </div>
      )}
      {matterSubmitBlockedReason && (
        <div className="mt-1">
          <InlineMessage variant="error">{matterSubmitBlockedReason}</InlineMessage>
        </div>
      )}
      {conversationSubmitBlockedReason && (
        <div className="mt-1">
          <InlineMessage variant="error">{conversationSubmitBlockedReason}</InlineMessage>
        </div>
      )}
      {errorMessage && (
        <div className="mt-1">
          <InlineMessage variant="error">{errorMessage}</InlineMessage>
        </div>
      )}
      {isRecovering && (
        <div className="mt-1">
          <InlineMessage>
            {recoveryStartedAfterDetachment ? 'Connection lost. Checking the status of your last request…' : 'Checking the status of your last request…'}
          </InlineMessage>
        </div>
      )}
    </div>
  )
}
